import type { KeyboardEvent } from "react";
import { Stack } from "@mui/material";
import { FoodsIcon, MRTGrid } from "@foodpilot/foods";
import { useTheme } from "@mui/material";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import type { AuditLogColumnVisibility, AuditLogDensity } from "./auditLogTablePreferences";
import { useAuditLogColumns } from "./useAuditLogColumns";

type AuditLogGridProps = {
    entries: AuditLogEntry[];
    isFetching: boolean;
    isFetchingNextPage: boolean;
    onRowClick: (entry: AuditLogEntry) => void;
    selectedEntryId: string | null;
    columnVisibility: AuditLogColumnVisibility;
    density: AuditLogDensity;
};

export const AuditLogGrid = (props: AuditLogGridProps) => {
    const { entries, isFetching, isFetchingNextPage, onRowClick, selectedEntryId, columnVisibility, density } = props;
    const theme = useTheme();
    const columns = useAuditLogColumns();
    const cellPadding = density === "compact" ? theme.spacing(1) : theme.spacing(2);

    return (
        <Stack flexGrow={1} minWidth={0}>
            <MRTGrid<AuditLogEntry>
                columns={columns}
                data={entries}
                getRowId={(entry) => entry.id}
                enablePagination={false}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                enableColumnResizing={false}
                enableSorting={false}
                enableRowSelection={false}
                enableRowActions
                positionActionsColumn="last"
                displayColumnDefOptions={{
                    "mrt-row-actions": { size: 48, header: "", muiTableHeadCellProps: { align: "center" } },
                }}
                renderRowActions={() => (
                    <Stack alignItems="center" justifyContent="center" aria-hidden="true">
                        <FoodsIcon icon="arrowRightShort" size={2} />
                    </Stack>
                )}
                state={{ columnVisibility, density, showProgressBars: isFetching || isFetchingNextPage }}
                muiTableBodyRowProps={({ row }) => ({
                    onClick: () => onRowClick(row.original),
                    onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                        if (event.key !== "Enter" && event.key !== " ") {
                            return;
                        }

                        event.preventDefault();
                        onRowClick(row.original);
                    },
                    role: "button",
                    tabIndex: 0,
                    selected: row.id === selectedEntryId,
                    sx: { cursor: "pointer" },
                })}
                muiTableBodyCellProps={{ sx: { paddingTop: cellPadding, paddingBottom: cellPadding } }}
                muiTableContainerProps={{ sx: { overflowX: "auto" } }}
            />
        </Stack>
    );
};
